import type { PersonaTiming } from '@/types/narrative';
import { formatDecimal } from '@/components/sizing/advancedInsightsShared';

type DutyCycleChartProps = {
  personaTimings: PersonaTiming[];
};

export const DutyCycleChart: React.FC<DutyCycleChartProps> = ({ personaTimings }) => {
  if (!personaTimings || personaTimings.length === 0) return null;

  return (
    <div className="space-y-4">
      {personaTimings.map((persona) => {
        const { readPct, thinkPct, queryPct } = persona.dutyCycleBreakdown;
        const idlePct = Math.max(0, 100 - readPct - thinkPct - queryPct);
        
        return (
          <div key={persona.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{persona.name}</span>
              <span className="text-xs text-muted-foreground">
                {formatDecimal(persona.cycleSec, 1)}s cycle
              </span>
            </div>
            
            {/* Horizontal stacked bar */}
            <div className="relative h-6 bg-muted rounded-lg overflow-hidden flex">
              {readPct > 0 && (
                <div 
                  className="bg-blue-500 flex items-center justify-center text-[10px] text-white font-medium transition-all"
                  style={{ width: `${readPct}%` }}
                  title={`Reading: ${formatDecimal(persona.readSeconds, 1)}s (${formatDecimal(readPct, 0)}%)`}
                >
                  {readPct > 10 && 'Read'}
                </div>
              )}
              {thinkPct > 0 && (
                <div 
                  className="bg-amber-500 flex items-center justify-center text-[10px] text-white font-medium transition-all"
                  style={{ width: `${thinkPct}%` }}
                  title={`Thinking: ${formatDecimal(persona.thinkSeconds, 1)}s (${formatDecimal(thinkPct, 0)}%)`}
                >
                  {thinkPct > 10 && 'Think'}
                </div>
              )}
              {queryPct > 0 && (
                <div 
                  className="bg-emerald-500 flex items-center justify-center text-[10px] text-white font-medium transition-all"
                  style={{ width: `${queryPct}%` }}
                  title={`Generating: ${formatDecimal(persona.serviceTimeSec, 1)}s (${formatDecimal(queryPct, 0)}%)`}
                >
                  {queryPct > 10 && 'Generate'}
                </div>
              )}
              {idlePct > 0 && (
                <div 
                  className="bg-muted-foreground/30 flex items-center justify-center text-[10px] text-muted-foreground font-medium transition-all"
                  style={{ width: `${idlePct}%` }}
                  title={`Idle: ${formatDecimal(idlePct, 0)}%`}
                >
                  {idlePct > 10 && 'Idle'}
                </div>
              )}
            </div>
            
            {/* Time breakdown */}
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded" />
                <span className="text-muted-foreground">Read {formatDecimal(persona.readSeconds, 1)}s</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-amber-500 rounded" />
                <span className="text-muted-foreground">Think {formatDecimal(persona.thinkSeconds, 1)}s</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-emerald-500 rounded" />
                <span className="text-muted-foreground">Gen {formatDecimal(persona.serviceTimeSec, 1)}s</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-muted-foreground/30 rounded" />
                <span className="text-muted-foreground">Active: {formatDecimal(queryPct, 0)}%</span>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Legend */}
      <div className="border-t border-border pt-3 mt-4">
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold">Active ratio</span> = Service Time / Cycle Time. 
          Only during the <span className="text-emerald-600 font-medium">generating</span> phase 
          are users counted as "active" for throughput sizing. However, KV cache remains resident 
          during <span className="text-blue-600 font-medium">reading</span> and{' '}
          <span className="text-amber-600 font-medium">thinking</span> phases.
        </p>
      </div>
    </div>
  );
};
